import { Phone, Mail } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

// Photos will be added shortly — drop each person's image URL into `photo`.
const CALL_URL = "https://calendly.com/hello-theditchcollective/speak-to-laura";

const TEAM = [
  {
    name: "Laura",
    role: "Founder & Director",
    photo: "/team/laura.png",
    email: ["hello@theditchcareers.com.au", "hello@theditchcollective.co.nz"],
    phone: "0448 176 400",
    bio: [
      "Laura is the Founder and Director of The Ditch Careers—the visionary who brought the business, its purpose and its people together.",
      "With more than 25 years of experience spanning recruitment, real estate, sales, marketing and operations, Laura has an exceptional ability to recognise potential, build meaningful relationships and turn ideas into action. Her career has taken her around the world, from establishing international offices to supporting community projects in South Africa and China, giving her a depth of experience that reaches far beyond business.",
      "The Ditch Careers is the culmination of everything Laura has built, learned and believed in throughout her career. She created it with a clear vision: to build a recruitment company that puts people first while still delivering exceptional commercial results.",
      "Laura leads with heart, courage and infectious energy. She has a rare talent for making people feel seen, bringing the right personalities together and inspiring those around her to believe in what is possible. For Laura, success isn't simply measured by roles filled—it's measured by the lives changed, businesses strengthened and relationships built along the way.",
    ],
  },
  {
    name: "Cara",
    role: "Co-Founder & Head of Growth",
    photo: "/team/cara.webp",
    email: ["info@theditchcareers.com.au", "info@theditchcollective.co.nz"],
    phone: "0449 784 402",
    bio: [
      "Cara is the person behind many of the ideas, employer relationships and systems that keep The Ditch Careers moving forward. With five years of experience across real estate, business development and digital marketing, she understands how to build strong partnerships and turn business needs into practical solutions.",
      "As Co-Founder and Head of Growth, Cara leads employer partnerships and business growth. She works closely with employers to understand their people, challenges and long-term goals, ensuring The Ditch Careers becomes a genuine extension of their business—not simply another recruitment provider.",
      "Behind the scenes, Cara also leads the development of the company's software, digital platforms and internal systems, creating smarter processes that make hiring simpler and more effective. A Kiwi now living in Australia, she brings energy, honesty and fresh thinking to everything she builds and she's never short of a new idea.",
    ],
  },
  {
    name: "Priya",
    role: "Co-Founder & Head of Recruitment",
    photo: "/team/priya.webp",
    email: ["careers@theditchcareers.com.au", "careers@theditchcollective.co.nz"],
    phone: "0428 763 221",
    bio: [
      "With more than 18 years of experience across recruitment, property and real estate in Australia and the UK, Priya has a genuine understanding of what makes people—and businesses—work well together.",
      "As Co-Founder and Head of Recruitment, Priya leads The Ditch Careers' recruitment strategy and oversees the experience of every employer and candidate we work with. She combines deep industry knowledge with strong commercial instincts and a refreshingly personal approach, taking the time to understand the person behind the CV and the business beyond the job description.",
      "Warm, driven and naturally good with people, Priya is known for building trusted relationships and spotting potential others might miss. For her, great recruitment isn't about filling roles—it's about creating connections that last.",
    ],
  },
  {
    name: "Holly",
    role: "Business Development Manager & Head of Marketing",
    photo: "/team/holly.webp",
    email: ["marketing@theditchcareers.com.au", "marketing@theditchcollective.co.nz"],
    phone: "0457 139 464",
    bio: [
      "Holly brings creativity, energy and a strong people-first approach to The Ditch Careers. With a natural ability to connect with others, she helps introduce employers to a more personal and effective way of finding the right people.",
      "As Business Development Manager and Head of Marketing, Holly supports employer growth while leading the company's marketing and social presence. From building new relationships to developing campaigns that bring the brand to life, she plays an important role in how The Ditch Careers connects with businesses and the wider community.",
      "Holly is enthusiastic, approachable and always ready to take on a new challenge. She brings fresh ideas to the team and is passionate about creating genuine connections that help both employers and The Ditch Careers grow.",
    ],
  },
];

const Avatar = ({ member }) => {
  if (member.photo) {
    return (
      <img
        src={member.photo}
        alt={member.name}
        data-testid={`team-photo-${member.name.toLowerCase()}`}
        className="h-56 w-44 rounded-2xl object-cover object-top shadow-md ring-1 ring-[#e3e0d3]"
      />
    );
  }
  return (
    <div
      data-testid={`team-photo-placeholder-${member.name.toLowerCase()}`}
      className="flex h-56 w-44 items-center justify-center rounded-2xl bg-[#0d2f4d] text-5xl font-heading font-bold text-[#C1CC24] shadow-md ring-1 ring-[#e3e0d3]"
    >
      {member.name.charAt(0)}
    </div>
  );
};

const TeamCard = ({ member, flip }) => {
  const key = member.name.toLowerCase();
  return (
    <article
      data-testid={`team-card-${key}`}
      className="grid gap-8 rounded-3xl border border-[#e3e0d3] bg-white p-8 shadow-sm sm:p-10 md:grid-cols-[auto_1fr]"
    >
      <div className={`flex flex-col items-center gap-5 ${flip ? "md:order-2" : ""}`}>
        <Avatar member={member} />
        <div className="flex w-full max-w-[16rem] flex-col gap-2.5">
          <a
            href={CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`team-call-${key}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] px-5 py-2.5 text-sm font-semibold text-[#0d2f4d] transition-colors hover:bg-[#aeb81f]"
          >
            <Phone className="h-4 w-4" /> Book a call
          </a>
          <a
            href={`mailto:${member.email.join(",")}?subject=${encodeURIComponent(`Hi ${member.name} — The Ditch Careers`)}`}
            data-testid={`team-email-${key}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0d2f4d] px-5 py-2.5 text-sm font-medium text-[#0d2f4d] transition-colors hover:bg-[#0d2f4d] hover:text-white"
          >
            <Mail className="h-4 w-4" /> Email
          </a>
        </div>
      </div>

      <div className={flip ? "md:order-1" : ""}>
        <h2 className="font-heading text-2xl font-bold text-[#0d2f4d]">{member.name}</h2>
        <p className="mt-1 font-accent text-lg text-[#7C8514]">{member.role}</p>
        <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-[#33475b]">
          {member.bio.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </article>
  );
};

export default function MeetTheTeam() {
  return (
    <div className="min-h-screen bg-[#f5f3ee]" data-testid="meet-the-team-page">
      <PublicHeader />
      <main className="mx-auto max-w-5xl px-6 pb-24 pt-28">
        <header className="mb-14 text-center">
          <p className="font-accent text-xl text-[#7C8514]">Meet the team</p>
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight text-[#0d2f4d] sm:text-5xl">
            The people behind The Ditch Careers
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[#51637a]">
            Recruitment that puts people first — led by a team who genuinely care about the lives changed,
            businesses strengthened and relationships built along the way.
          </p>
        </header>

        <div className="space-y-8">
          {TEAM.map((member, i) => (
            <TeamCard key={member.name} member={member} flip={i % 2 === 1} />
          ))}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
